import "./locationAdminDetailsAddComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import {CommonService} from "../../../../shared/services";
import Grid from "@mui/material/Grid";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import commonService from "../../../../shared/services/common.service";
import {useSearchParams} from "react-router-dom";
import LinkComponent from "../../../../shared/components/link/LinkComponent";
import useRedirectUser from "../../../../shared/hooks/useRedirectUser";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";

interface LocationAdminDetailsAddComponentProps {
    locationId: number;
    next: Function;
    cancel: Function;
    prev: Function;
    locationDetails: any;
}

const validationSchema = Yup.object({
    adminId: Yup.mixed().nullable()
    // .required('Location Admin Required'),
});


interface LocationAdminDetailsFormSchema {
    adminId: string,
}

const formInitialValues: LocationAdminDetailsFormSchema = {
    adminId: "",
}


const LocationAdminDetailsAddComponent = (props: LocationAdminDetailsAddComponentProps) => {
    const {next, prev, cancel, locationDetails, locationId} = props;
    const {handleRedirectUserModule} = useRedirectUser();
    const [adminDetailsFormInitialValues, setAdminDetailsFormInitialValues] = useState<LocationAdminDetailsFormSchema>(formInitialValues)
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");
    const [locationAdminsList, setLocationAdminsList] = useState<any>([]);
    const [locationAdminsListLoading, setLocationAdminsListLoading] = useState<boolean>(false);
    const [locationAdminListLoadingFailed, setLocationAdminListLoadingFailed] = useState<boolean>(false);
    const [locationAdminListLoaded, setLocationAdminListLoaded] = useState<boolean>(false);


    const onSubmit = useCallback((values: any, {
        setSubmitting,
    }: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = {adminId: values?.adminId?.id || null, lastCompletedStep: lastCompletedStep}
        CommonService._location.editLocationDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                next(response?.data);
            })
            .catch((error: any) => {
                setSubmitting(false);
                CommonService._alert.showToast('Error adding admin details', "error");
            })
    }, [locationId, next, lastCompletedStep]);

    const redirectUser = useCallback(() => {
        handleRedirectUserModule({type: 'locationAdmin'})
    }, [handleRedirectUserModule])

    useEffect(() => {
        if (locationDetails?.adminDetails && Object.keys(locationDetails.adminDetails).length) {
            setAdminDetailsFormInitialValues({adminId: locationDetails?.adminDetails});
        }
    }, [locationDetails]);

    const getLocationAdminsList = useCallback(() => {
        setLocationAdminsListLoading(true);
        setLocationAdminListLoaded(false);
        setLocationAdminListLoadingFailed(false)
        commonService._location.getLocationAdminsList({})
            .then((response: IAPIResponseType<any>) => {
                setLocationAdminsList(response.data);
                setLocationAdminListLoaded(true);
                setLocationAdminsListLoading(false);
            })
            .catch((error: any) => {
                setLocationAdminListLoadingFailed(true);
                setLocationAdminsListLoading(false);
            });
    }, [])

    useEffect(() => {
        getLocationAdminsList();
    }, [getLocationAdminsList]);


    return (
        <div className={'location-admin-details-add-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Admin Details'}
                handleClick={prev}
                tooltipData={"These details relate to the person responsible for administering this location."}
            />

            <div className="t-form t-form-back-navigation">
                <Formik
                    validationSchema={validationSchema}
                    initialValues={adminDetailsFormInitialValues}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={true}
                    enableReinitialize={true}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, values, resetForm, isValid, errors, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"location-admin-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'adminId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Location Admin'}
                                                            placeholder={'Select Location Admin'}
                                                            isDataLoaded={locationAdminListLoaded}
                                                            isDataLoading={locationAdminsListLoading}
                                                            isDataLoadingFailed={locationAdminListLoadingFailed}
                                                            // required={true}
                                                            formikField={field}
                                                            data={locationAdminsList}
                                                            valueKey={'id'}
                                                            getOptionLabel={(option => commonService.getFullName(option) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <div className="redirect-new-link-wrapper">
                                                <div>Didn't find admin?</div>
                                                <LinkComponent className={"redirect-link"}
                                                               onClick={redirectUser}>Create Admin</LinkComponent>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <FeedbackComponent className={"mrg-top-10 mrg-bottom-10"}>
                                                Adding an admin is recommended, but if unavailable, it can be skipped
                                                and added later.
                                            </FeedbackComponent>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>

                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LocationAdminDetailsAddComponent;
